import { t } from "@lingui/core/macro";
import { PrintData } from "qz-tray";
import { BrowserPrintData, createA4HtmlPrintData } from "./a4";
import { createPrinterAdapter } from "./adapters";
import { PrintableDocument, PrinterConfig } from "./types";
import { renderDocumentToEscPos, renderDocumentToHtml } from "./renderer";
import { DevicePrinterUserError } from "./printer-errors";
import { buildDevicePrinterRenderConfig } from "./render-config";
import { escPosCommandsToDeviceBytes } from "./device-bytes";
import { useDevicePrinterStore } from "@/stores/device-printer-store";
import { isNativePlatform } from "@/utils/native";

export * from "./types";
export * from "./a4";
export * from "./browser-print";
export * from "./utils";
export * from "./adapters";
export * from "./bluetooth";
export * from "./sunmi";
export * from "./printer-errors";
export * from "./render-config";
export * from "./device-bytes";

/**
 * ESC/POS bytes destined for the device-local printer instead of QZ Tray.
 * Produced by `preparePrintData` when running in the mobile app; which
 * transport carries them is decided by the selected printer.
 */
export type DevicePrintData = {
  type: "device-escpos";
  bytes: Uint8Array;
};

export type PreparedPrintData = PrintData | DevicePrintData | BrowserPrintData;

export async function preparePrintData(
  doc: PrintableDocument,
  config: PrinterConfig,
  additionalSettings?: {
    receiptHeader?: string;
    receiptFooter?: string;
  },
): Promise<PreparedPrintData> {
  // In the mobile app receipts always print on the device's own printer — the
  // location's QZ Tray printer config is ignored.
  if (isNativePlatform()) {
    const devicePrinter = useDevicePrinterStore.getState().selectedPrinter;

    if (!devicePrinter) {
      throw new DevicePrinterUserError(
        t`Tiskalnik ni izbran. Izberite ga v nastavitvah davčne blagajne.`,
      );
    }

    const escPosCommands = await renderDocumentToEscPos(
      doc,
      buildDevicePrinterRenderConfig(devicePrinter),
      additionalSettings,
    );

    return {
      type: "device-escpos",
      bytes: escPosCommandsToDeviceBytes(escPosCommands),
    };
  }

  if (!config) {
    throw new Error("Printer config is required");
  }

  if (config.printerType === "a4") {
    const receiptHtml = await renderDocumentToHtml(
      doc,
      config,
      additionalSettings,
    );
    return createA4HtmlPrintData(receiptHtml, config.htmlRootFontSize || 12);
  }

  const adapter = createPrinterAdapter(config);

  if (config.printerType === "escpos_generic") {
    const escPosCommands = await renderDocumentToEscPos(
      doc,
      config,
      additionalSettings,
    );
    // Convert commands to QZ Tray PrintData
    return adapter.adapt(escPosCommands);
  } else if (config.printerType === "star_mi") {
    const htmlString = await renderDocumentToHtml(
      doc,
      config,
      additionalSettings,
    );
    // Convert to QZ Tray PrintData
    return adapter.adapt(htmlString);
  }

  throw new Error(`Unsupported printer type: ${config.printerType}`);
}
